import { useState } from "react";
import Header from "../components/Header";

export default function Tracker() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [player, setPlayer] = useState(null);

  async function handleSearch(e) {
    e.preventDefault();
    setLoading(true);
    setError("");
    setPlayer(null);

    try {
      const res = await fetch(
        `/api/player-data?query=${encodeURIComponent(query)}`,
      );
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || "Failed to load player data");

      setPlayer(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const d = player?.data;

  return (
    <main className="min-h-screen bg-[#050505] text-white overflow-hidden">
      <Header />

      <section className="relative min-h-screen px-6 pt-32 pb-20 md:px-[7%]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(255,217,120,0.25),transparent_35%),radial-gradient(circle_at_bottom_right,rgba(255,143,107,0.22),transparent_40%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_top,#050505_0%,transparent_50%)]" />

        <div className="relative z-10">
          <h1 className="mt-2 text-[clamp(54px,9vw,120px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
            Outfall Tracker
          </h1>

          <p className="mt-5 max-w-2xl text-lg font-bold text-white/80 md:text-xl">
            Search a Roblox username or user ID to view saved Outfall profile
            data.
          </p>

          <form
            onSubmit={handleSearch}
            className="mt-8 flex max-w-2xl flex-col gap-3 sm:flex-row"
          >
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Username or User ID"
              className="w-full rounded-2xl border border-[#ffb56f]/30 bg-black/50 px-5 py-4 font-black text-white outline-none backdrop-blur-md placeholder:text-white/40 focus:border-[#ffd978]"
            />

            <button
              disabled={loading || !query.trim()}
              className="outfall-button disabled:opacity-50"
            >
              {loading ? "Searching..." : "Track"}
            </button>
          </form>

          {error && (
            <div className="mt-6 max-w-2xl rounded-2xl border border-red-500/40 bg-red-500/10 p-4 font-bold text-red-200">
              {error}
            </div>
          )}

          {player && d && (
            <div className="mt-10 grid gap-6">
              <section className="outfall-card bg-black/45 p-6 backdrop-blur-md">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-black uppercase text-[#ffb56f]">
                      Player
                    </p>
                    <h2 className="text-4xl text-[#ffd978]">
                      {player.username || "Unknown"}
                    </h2>
                    <p className="mt-1 font-black text-white/60">
                      ID: {player.userId} • Key: beta2_{player.userId}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-[#ffd978]/30 bg-[#ffd978]/10 px-5 py-3 text-right">
                    <p className="text-sm font-black uppercase text-white/60">
                      Tokens
                    </p>
                    <p className="text-3xl font-black text-[#ffd978]">
                      {(d.Currency?.Tickets || 0).toLocaleString("en-GB")}
                    </p>
                  </div>
                </div>
              </section>

              <section className="grid gap-6 lg:grid-cols-4">
                {[
                  ["Level", d.Ranking?.Level],
                  ["XP", d.Ranking?.LevelXP],
                  ["Rank", d.Ranking?.Rank],
                  ["Season", d.Ranking?.CurrentSeason],
                ].map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-2xl border border-[#ffb56f]/30 bg-black/45 p-5 text-center backdrop-blur-md"
                  >
                    <p className="text-3xl font-black text-[#ffd978]">
                      {value ?? 0}
                    </p>
                    <p className="mt-1 text-sm font-black uppercase text-white">
                      {label}
                    </p>
                  </div>
                ))}
              </section>

              <section className="grid gap-6 lg:grid-cols-3">
                <Panel title="Stats">
                  <Stat label="Wins" value={d.Stats?.Wins} />
                  <Stat label="Kills" value={d.Stats?.Kills} />
                  <Stat label="Deaths" value={d.Stats?.Deaths} />
                  <Stat label="Damage" value={d.Stats?.Damage} />
                  <Stat
                    label="Play Time"
                    value={`${d.Stats?.PlayTime || 0} mins`}
                  />
                  <Stat label="Most Kills" value={d.Stats?.MostKills} />
                  <Stat label="Furthest Kill" value={d.Stats?.FurthestKill} />
                  <Stat label="Gold Medals" value={d.Stats?.GoldMedals} />
                  <Stat label="Silver Medals" value={d.Stats?.SilverMedals} />
                  <Stat label="Bronze Medals" value={d.Stats?.BronzeMedals} />
                </Panel>

                <Panel title="Equipped">
                  {Object.entries(d.Equipped || {}).map(([key, value]) => (
                    <Stat key={key} label={key} value={value} />
                  ))}
                </Panel>

                <Panel title="Battle Pass">
                  <Stat
                    label="Current Pass"
                    value={d.BattlePass?.CurrentPass}
                  />
                  <Stat label="XP Boost" value={d.BattlePass?.XPBoost} />
                </Panel>
              </section>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function Panel({ title, children }) {
  return (
    <article className="outfall-card bg-black/45 p-6 backdrop-blur-md">
      <h3 className="mb-5 text-4xl text-[#ffd978]">{title}</h3>
      <div className="space-y-3">{children}</div>
    </article>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-white/10 pb-2">
      <span className="font-black text-white/60">{label}</span>
      <span className="text-right font-black text-white">{value ?? 0}</span>
    </div>
  );
}
